import type { LoginFormData, RegisterFormData } from '@/types';

// Backend Auth API URL (via Caddy proxy)
const AUTH_API_URL = '/api/auth';

// =====================================================
// AUTH TOKEN MANAGEMENT
// =====================================================

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

export function saveAuthToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function saveUser(user: any) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getUser() {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
}

// =====================================================
// AUTHENTICATION FUNCTIONS (Backend API)
// =====================================================

export async function signIn(credentials: LoginFormData, saveSession: boolean = false) {
  const { email, password } = credentials;

  // Call backend auth login endpoint
  const response = await fetch(`${AUTH_API_URL}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      password,
      device_fingerprint: generateDeviceFingerprint(),
      user_agent: navigator.userAgent
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Login failed');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Invalid credentials');
  }

  // SECURITY FIX: Only save token if explicitly requested
  // This allows device verification to complete before storing credentials
  if (saveSession) {
    saveAuthToken(data.token);
    saveUser(data.user);
  }

  return {
    user: data.user,
    session: {
      access_token: data.token,
      expires_at: data.expires_at
    }
  };
}

/**
 * Complete the login by saving the session to localStorage
 * Call this after device verification is complete
 */
export function completeLogin(token: string, user: any) {
  saveAuthToken(token);
  saveUser(user);
}

export async function signUp(credentials: RegisterFormData) {
  const { email, password, full_name, study_association_id, member_id } = credentials;

  // Call backend auth register endpoint
  const response = await fetch(`${AUTH_API_URL}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      password,
      full_name,
      study_association_id: study_association_id || null,
      member_id: member_id || null
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Registration failed');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Registration failed');
  }

  return {
    user: { id: data.user_id, email },
    message: data.message
  };
}

export async function signOut() {
  const token = getAuthToken();
  const user = getUser();

  if (token) {
    // Call backend auth logout endpoint
    await fetch(`${AUTH_API_URL}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token })
    });
  }

  // 2026-07-02 (task #301): Rens alle autosave-drafts for denne brukeren
  // så cache ikke lekker til neste bruker på samme nettleser.
  if (user?.id) {
    try {
      const { clearAllDraftsForUser } = await import('../hooks/useAutosaveDraft');
      clearAllDraftsForUser(user.id);
    } catch (err) {
      console.warn('[signOut] Kunne ikke rense drafts:', err);
    }
  }

  // Clear local storage
  clearAuthToken();
}

export async function getCurrentUser() {
  const token = getAuthToken();

  if (!token) {
    return null;
  }

  // Verify token with backend
  const response = await fetch(`${AUTH_API_URL}/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token })
  });

  if (!response.ok) {
    clearAuthToken();
    return null;
  }

  const data = await response.json();

  if (!data.success) {
    clearAuthToken();
    return null;
  }

  // Update user in localStorage
  saveUser(data.user);

  return data.user;
}

export async function requestPasswordReset(email: string) {
  const response = await fetch(`${AUTH_API_URL}/request-password-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to request password reset');
  }

  const data = await response.json();
  return data;
}

export async function resetPassword(token: string, newPassword: string) {
  const response = await fetch(`${AUTH_API_URL}/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      new_password: newPassword
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reset password');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Password reset failed');
  }

  return data;
}

export async function verifyEmail(token: string) {
  const response = await fetch(`${AUTH_API_URL}/verify-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify email');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Email verification failed');
  }

  return data;
}

export async function resendVerificationEmail(email: string) {
  const response = await fetch(`${AUTH_API_URL}/resend-verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to resend verification email');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to resend verification email');
  }

  return data;
}

export async function updatePassword(_newPassword: string) {
  // TODO: Implement password update for logged-in users
  throw new Error('Password update not yet implemented');
}

// =====================================================
// API REQUEST HELPER WITH AUTHENTICATION
// =====================================================

/**
 * Helper function to make authenticated API requests
 * Automatically adds Authorization header with JWT token
 */
export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getAuthToken();

  // Check if body is FormData (for file uploads)
  const isFormData = options.body instanceof FormData;

  // Merge headers with authentication
  // Don't set Content-Type for FormData - browser will set it with boundary
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> || {}),
  };

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // If unauthorized, clear token and redirect to login
  if (response.status === 401) {
    clearAuthToken();
    window.location.href = '/login';
  }

  return response;
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

function generateDeviceFingerprint(): string {
  const nav = navigator;
  const screen = window.screen;

  const components = [
    nav.userAgent,
    nav.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
  ];

  const fingerprint = components.join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(36);
}
